import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '../components/ui/Button';

interface SentryContextValue {
  lastEventId: string | null;
  captureError: (
    error: Error,
    context?: Record<string, unknown>
  ) => string;
  addBreadcrumb: (
    message: string,
    data?: Record<string, unknown>
  ) => void;
}

const SentryContext = createContext<SentryContextValue>({
  lastEventId: null,
  captureError: () => '',
  addBreadcrumb: () => {},
});

export function useSentry() {
  return useContext(SentryContext);
}

interface SentryProviderProps {
  children: React.ReactNode;
}

export function SentryProvider({
  children,
}: SentryProviderProps) {
  const [lastEventId, setLastEventId] = useState<
    string | null
  >(null);

  useEffect(() => {
    Sentry.addEventProcessor(event => {
      if (event.event_id) {
        setLastEventId(event.event_id);
        document.body.dataset.sentryEventId =
          event.event_id;
      }
      return event;
    });
  }, []);

  const captureError = useCallback(
    (
      error: Error,
      context?: Record<string, unknown>
    ): string => {
      const eventId = Sentry.captureException(error, {
        extra: context,
      });
      setLastEventId(eventId);
      document.body.dataset.sentryEventId = eventId;
      return eventId;
    },
    []
  );

  const addBreadcrumb = useCallback(
    (message: string, data?: Record<string, unknown>) => {
      Sentry.addBreadcrumb({
        category: 'ui.interaction',
        message,
        level: 'info',
        data,
      });
    },
    []
  );

  return (
    <SentryContext.Provider
      value={{ lastEventId, captureError, addBreadcrumb }}
    >
      {children}
    </SentryContext.Provider>
  );
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactElement;
}

function DefaultErrorFallback() {
  const sentryEventId = document.body.dataset.sentryEventId;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold mb-4">
          Something went wrong
        </h1>
        <p className="text-gray-400 mb-4">
          An error has been reported and we're looking into
          it.
        </p>
        <div className="space-y-2 mb-6">
          {sentryEventId && (
            <p className="text-sm text-gray-500">
              Sentry Event ID:{' '}
              <code className="bg-gray-800 px-2 py-1 rounded">
                {sentryEventId}
              </code>
            </p>
          )}
          <p className="text-sm text-gray-600 mb-4">
            Error ID: {sentryEventId}
          </p>
          <p className="text-xs text-gray-600">
            Check browser console for Netlify trace ID
          </p>
        </div>
        <Button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Reload Page
        </Button>
      </div>
    </div>
  );
}

export function SentryErrorBoundary({
  children,
  fallback,
}: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback || <DefaultErrorFallback />}
      onError={(error, componentStack) => {
        const eventId = Sentry.lastEventId();
        if (eventId) {
          document.body.dataset.sentryEventId = eventId;
        }
        console.error(
          '[SentryErrorBoundary] Caught error:',
          error
        );
        console.error(
          '[SentryErrorBoundary] Component stack:',
          componentStack
        );
        if (eventId) {
          console.log(
            `[SentryErrorBoundary] Sentry Event ID: ${eventId}`
          );
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
